import {
  SpecialTag,
  hideStrikethroughPrice as hideStrikethrough,
  productPhotoMetadata,
} from '@iheartjane/menu';
import {
  Box,
  Flex,
  Image,
  JaneGoldIcon,
  TagFilledIcon,
  TagIcon,
  Typography,
  useTabletMediaQuery,
} from '@iheartjane/reefer';
import { formatCurrency, titleCase } from '@iheartjane/utils';
import { useMemo } from 'react';

import {
  useNeedsSignInForEligibility,
  useShouldShowGold,
} from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct } from '@jane/shared/models';
import { calculateDiscountPercentage } from '@jane/shared/util';

export const CheckoutReviewProductCard = ({
  bloomUserExperience,
  discountedPrice,
  originalPrice,
  product,
  store,
}: {
  bloomUserExperience?: boolean;
  discountedPrice?: number;
  originalPrice?: number;
  product: PendingCartProduct;
  store: any;
}) => {
  const isMobile = useTabletMediaQuery({ width: 'max' });

  const shouldShowGold = useShouldShowGold({
    ...(store.id ? { store: { id: store.id, state: store.state } } : {}),
  });

  const {
    name,
    brand,
    brand_subtype,
    category,
    brand_discount_label,
    brand_discount_title,
    count,
    special_title,
  } = product;
  const brandDiscountText = brand_discount_title || brand_discount_label;

  const needsSignInForEligibility = useNeedsSignInForEligibility({
    cartProduct: product,
  });

  const { url } = productPhotoMetadata(product);

  const isDiscounted = Boolean(
    discountedPrice && originalPrice !== discountedPrice
  );

  const discountPercentage = calculateDiscountPercentage(
    originalPrice,
    discountedPrice
  );

  const hideStrikethroughPrice = useMemo(
    () => hideStrikethrough(store?.state || ''),
    [store?.state]
  );

  return (
    <Flex flexDirection={isMobile ? 'column' : 'row'}>
      {bloomUserExperience ? (
        <Flex width="96px" height="96px">
          <Flex position="absolute">
            <Image
              borderRadius="rounded"
              height="96px"
              width="96px"
              responsive
              sizes="thumbnail"
              altText={`${name}-image`}
              src={url}
            />
          </Flex>
          {discountPercentage && discountPercentage > 0 && (
            <Flex
              zIndex={1}
              position="relative"
              minWidth="96px"
              justifyContent="flex-end"
              p={8}
            >
              <SpecialTag
                label={
                  hideStrikethroughPrice ? 'Deal' : `-${discountPercentage}%`
                }
              />
            </Flex>
          )}
        </Flex>
      ) : (
        <Image
          borderRadius="rounded"
          height="96px"
          width="96px"
          responsive
          sizes="thumbnail"
          altText={`${name}-image`}
          src={url}
        />
      )}

      <Box ml={isMobile ? 0 : 16} mt={isMobile ? 16 : 0} width="100%">
        <Flex>
          <Typography variant="body-bold">
            {count > 1 ? `${count} x ` : ''}
            {name}
          </Typography>
          {!bloomUserExperience ? (
            <Box
              ml="auto"
              mb={isDiscounted && !hideStrikethroughPrice ? -24 : 0}
            >
              {(!isDiscounted || !hideStrikethroughPrice) && (
                <Typography
                  strikeThrough={!!isDiscounted}
                  variant={isDiscounted ? 'body' : 'body-bold'}
                >
                  {formatCurrency(originalPrice)}
                </Typography>
              )}
              {isDiscounted && (
                <Typography variant="body-bold" color="text-main">
                  {formatCurrency(discountedPrice)}
                </Typography>
              )}
            </Box>
          ) : (
            // for bloom
            <Box
              ml="auto"
              mb={discountedPrice && !hideStrikethroughPrice ? -24 : 0}
            >
              {Boolean(discountedPrice) && (
                <Typography
                  variant="body-bold"
                  color={special_title ? 'info' : 'text-main'}
                >
                  {formatCurrency(discountedPrice)}
                </Typography>
              )}
              {formatCurrency(discountedPrice) !==
                formatCurrency(originalPrice) &&
                (!discountedPrice || !hideStrikethroughPrice) && (
                  <Typography
                    strikeThrough={!!discountedPrice}
                    variant={discountedPrice ? 'body' : 'body-bold'}
                    color={discountedPrice ? 'text-light' : 'text-main'}
                  >
                    {formatCurrency(originalPrice)}
                  </Typography>
                )}
            </Box>
          )}
        </Flex>

        {!!brand && <Typography color="text-light">{brand}</Typography>}

        {(!!brand_subtype || !!category) && (
          <Typography color="text-light">
            {brand_subtype || ''}
            {brand_subtype && category ? ' - ' : ''}
            {titleCase(category || '')}
          </Typography>
        )}

        {!!special_title && (!bloomUserExperience || isDiscounted) && (
          <Flex alignItems="center">
            {bloomUserExperience && <TagFilledIcon size="sm" color="info" />}
            {!bloomUserExperience && <TagIcon size="sm" color="info" />}
            <Typography
              color={bloomUserExperience ? 'text-main' : 'info'}
              ml={4}
            >
              {special_title}
            </Typography>
          </Flex>
        )}

        {!!brandDiscountText && !!shouldShowGold && (
          <Flex alignItems="center" width="100%">
            <JaneGoldIcon altText="jane-gold-icon" size="sm" />
            <Flex ml={4} gap={4} alignItems="center">
              <Typography>{brandDiscountText}</Typography>
              {needsSignInForEligibility && (
                <Typography color="grays-mid">
                  (Sign in for eligibility)
                </Typography>
              )}
            </Flex>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
